import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';

import { formatUnreadBadge, TAB_ITEMS } from '@/components/nav-items';
import { useDiscussionsBadge } from '@/features/tonight/nav-badges';
import { useLocaleOptional } from '@/i18n/locale';
import { pressable } from '@/lib/motion';
import { cn } from '@/lib/utils';

const MotionNavLink = motion.create(NavLink);

/** The unread count dot — coral (`danger`), not amber (ui-ux.md §2). */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-hidden="true"
      className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-micro tabular-nums text-on-danger"
    >
      {formatUnreadBadge(count)}
    </span>
  );
}

/**
 * Mobile (< md) tab bar: `surface`, safe-area padded, a single hairline top
 * edge for platform familiarity (ui-ux.md §3). Five even-width tabs —
 * Tonight · Library · Discover · Discussions (unread replies badge) · Profile;
 * the active one is the accent pill (`text-accent-ink` on `bg-accent-soft`),
 * matching the rail. The tabs are links, but a long-press selects nothing and
 * opens no link-preview callout — a native tab bar never does. Notifications
 * (follow events) are the bell on Tonight's masthead.
 */
export function BottomTabs() {
  const discussionsUnread = useDiscussionsBadge();
  const { t } = useLocaleOptional();

  return (
    <nav
      aria-label={t('nav.primary')}
      className="fixed inset-x-0 bottom-0 z-40 select-none border-t border-border bg-surface pb-safe [-webkit-touch-callout:none] md:hidden"
    >
      <div className="grid grid-cols-5">
        {TAB_ITEMS.map(({ to, labelKey, icon: Icon, end, showUnreadBadge }) => {
          const count = showUnreadBadge ? discussionsUnread : 0;
          const label = t(labelKey);
          return (
            <MotionNavLink
              key={to}
              to={to}
              end={end}
              aria-label={
                count > 0 ? t('nav.unreadReplies', { label, count: formatUnreadBadge(count) }) : label
              }
              className={({ isActive }) =>
                cn(
                  'mx-0.5 flex min-h-[56px] flex-col items-center justify-center gap-1 rounded px-1 pb-1 pt-1.5 text-micro transition-colors',
                  isActive
                    ? 'bg-accent-soft text-ink [&_svg]:text-accent-ink'
                    : 'text-ink-secondary hover:bg-surface-alt hover:text-ink',
                )
              }
              {...pressable()}
            >
              <span className="relative">
                <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                {count > 0 && <UnreadBadge count={count} />}
              </span>
              {label}
            </MotionNavLink>
          );
        })}
      </div>
    </nav>
  );
}
